import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Download, FileSpreadsheet } from "lucide-react";
import api, { API } from "@/lib/api";
import { AdminLayout } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/contexts/AuthContext";

export default function AdminReports() {
  const { user } = useAuth();
  const [orgs, setOrgs] = useState([]);
  const [locs, setLocs] = useState([]);
  const [ats, setAts] = useState([]);
  const [items, setItems] = useState([]);
  const [filters, setFilters] = useState({ organization_id: "", location_id: "", activity_type_id: "", start: "", end: "" });
  const [includePersonal, setIncludePersonal] = useState(false);

  useEffect(() => {
    api.get("/organizations").then(r => setOrgs(r.data));
    api.get("/locations").then(r => setLocs(r.data));
    api.get("/activity-types").then(r => setAts(r.data));
  }, []);

  const params = useMemo(() => Object.fromEntries(Object.entries(filters).filter(([_,v])=>v)), [filters]);
  useEffect(() => { api.get("/admin/registrations", { params }).then(r => setItems(r.data)); }, [params]);

  const download = async (format) => {
    try {
      const token = localStorage.getItem("access_token");
      const res = await api.get("/admin/export", { params: { ...params, format, include_personal: includePersonal }, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = `samtalsraknaren.${format === "xlsx" ? "xlsx" : "csv"}`;
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
      toast.success("Nedladdning startad");
    } catch (e) { toast.error(e.response?.data?.detail || "Export misslyckades"); }
  };

  return (
    <AdminLayout title="Rapporter & export" subtitle="Filtrera och exportera registrerad data">
      <div className="bg-white border border-slate-200 rounded-2xl p-5 mb-4 grid grid-cols-1 md:grid-cols-6 gap-3 items-end">
        <div><Label className="text-xs">Arbetarekommun</Label>
          <Select value={filters.organization_id || "all"} onValueChange={v => setFilters(f => ({ ...f, organization_id: v === "all" ? "" : v }))}>
            <SelectTrigger data-testid="rep-org"><SelectValue placeholder="Alla"/></SelectTrigger>
            <SelectContent><SelectItem value="all">Alla</SelectItem>{orgs.map(o=><SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>)}</SelectContent>
          </Select></div>
        <div><Label className="text-xs">Plats</Label>
          <Select value={filters.location_id || "all"} onValueChange={v => setFilters(f => ({ ...f, location_id: v === "all" ? "" : v }))}>
            <SelectTrigger data-testid="rep-loc"><SelectValue placeholder="Alla"/></SelectTrigger>
            <SelectContent><SelectItem value="all">Alla</SelectItem>{locs.map(o=><SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>)}</SelectContent>
          </Select></div>
        <div><Label className="text-xs">Aktivitetstyp</Label>
          <Select value={filters.activity_type_id || "all"} onValueChange={v => setFilters(f => ({ ...f, activity_type_id: v === "all" ? "" : v }))}>
            <SelectTrigger data-testid="rep-at"><SelectValue placeholder="Alla"/></SelectTrigger>
            <SelectContent><SelectItem value="all">Alla</SelectItem>{ats.map(o=><SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>)}</SelectContent>
          </Select></div>
        <div><Label className="text-xs">Från</Label><Input data-testid="rep-start" type="date" value={filters.start} onChange={e => setFilters(f => ({ ...f, start: e.target.value }))}/></div>
        <div><Label className="text-xs">Till</Label><Input data-testid="rep-end" type="date" value={filters.end} onChange={e => setFilters(f => ({ ...f, end: e.target.value }))}/></div>
        <div className="flex flex-col gap-1">
          {user?.role === "central_admin" && (<div className="flex items-center gap-2 text-xs"><Switch data-testid="rep-personal" checked={includePersonal} onCheckedChange={setIncludePersonal}/><span>Inkl. personuppgifter</span></div>)}
        </div>
      </div>

      <div className="flex gap-2 mb-4">
        <Button data-testid="rep-csv" variant="outline" onClick={() => download("csv")}><Download size={14} className="mr-1"/>Exportera CSV</Button>
        <Button data-testid="rep-xlsx" variant="outline" onClick={() => download("xlsx")}><FileSpreadsheet size={14} className="mr-1"/>Exportera Excel</Button>
        <div className="ml-auto text-sm text-slate-500 self-center">{items.length} registreringar</div>
      </div>

      <div className="bg-white border border-slate-200 rounded-2xl overflow-x-auto">
        <Table>
          <TableHeader><TableRow>
            <TableHead>Datum</TableHead><TableHead>Arbetarekommun</TableHead><TableHead>Aktivitetstyp</TableHead>
            <TableHead>Plats</TableHead>{includePersonal && <TableHead>Medlem</TableHead>}<TableHead className="text-right">Antal samtal</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {items.map(i => (
              <TableRow key={i.id} data-testid={`rep-row-${i.id}`}>
                <TableCell>{i.activity_date}</TableCell>
                <TableCell>{i.organization_name}</TableCell>
                <TableCell>{i.activity_type_name}</TableCell>
                <TableCell>{i.location_name}</TableCell>
                {includePersonal && <TableCell>{i.member_name} <span className="text-slate-400 text-xs">{i.member_email}</span></TableCell>}
                <TableCell className="text-right font-heading font-bold number-tabular">{i.conversation_count}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </AdminLayout>
  );
}
